import { Link } from '@tanstack/react-router'
import { LuSend, LuGlobe } from 'react-icons/lu'
import { FaMosque } from 'react-icons/fa6'
import { Button } from '#/components/ui/button'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '#/components/ui/breadcrumb'
import { RevealItem, ScrollReveal } from '#/components/common/ScrollReveal'
import { TextAnimate } from '#/components/common/TextAnimate'
import bismillah from '#/assets/bismillah.svg'
import courseImgOutline from '#/assets/courses/course-img-mask-outline.svg'
import courseHeroDefault from '#/assets/courses/course-1.png'
import type { Course } from '#/features/courses/types/course.types'
import { LucideBarChart2 } from 'lucide-react'
import courseImg from '#/assets/courses/course-hero.webp'

import { getImageUrl } from '#/lib/imageUrl'

interface CourseHeroSectionProps {
  course?: Course
}

export default function CourseHeroSection({ course }: CourseHeroSectionProps) {
  const level = course?.level || 'All Levels'
  const language = course?.language || 'Arabic & English'
  const title = course?.title || 'Course Details'
  const description = course?.shortDescription || ''
  const image = getImageUrl(course?.image, courseHeroDefault)
  const tagLabel = course?.category?.name || course?.tags?.[0]?.name || 'Course'

  const courseMeta = [
    { icon: LucideBarChart2, label: 'Level', value: level },
    { icon: LuGlobe, label: 'Language', value: language },
  ]

  return (
    <section
      className="relative py-30 w-full flex flex-col justify-center"
      id="course-hero"
    >
      <div className="hero-mask absolute inset-0 z-0">
        <img
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-0 size-full object-cover"
          src={courseImg}
        />
        <div className="absolute inset-0 z-10 bg-primary-900/82" />
      </div>

      <div className="container relative z-10 mx-auto grid grid-cols-1 lg:grid-cols-2 w-full items-center gap-10">
        {/* ── Left column ── */}
        <ScrollReveal className="flex flex-col items-start gap-8">
          {/* Breadcrumb */}
          <RevealItem>
            <Breadcrumb>
              <BreadcrumbList className="font-semibold text-white">
                <BreadcrumbItem>
                  <BreadcrumbLink
                    asChild
                    className="text-white hover:text-primary-300"
                  >
                    <Link to="/" className="flex items-center gap-2">
                      <FaMosque />
                      Home
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator className="text-primary-500" />
                <BreadcrumbItem>
                  <BreadcrumbLink
                    asChild
                    className="text-white/80 hover:text-primary-300"
                  >
                    <Link to="/courses">Courses</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator className="text-primary-500" />
                <BreadcrumbItem>
                  <BreadcrumbPage className="text-primary-400">
                    {title}
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </RevealItem>

          <div className="flex flex-col gap-5 w-full">
            {/* Bismillah */}
            <RevealItem>
              <img
                alt=""
                src={bismillah}
                width={160}
                height={60}
                className="w-40 max-sm:w-32"
              />
            </RevealItem>

            {/* Category / Tag pill + Title */}
            <RevealItem>
              <div className="inline-flex items-center rounded-full bg-primary-500/20 border border-primary-500/40 px-4 py-1.5 text-sm font-semibold uppercase tracking-widest text-primary-300 mb-3">
                {tagLabel}
              </div>
              <TextAnimate
                by="word"
                animation="blurInUp"
                as="h1"
                className="text-5xl font-bold text-white xl:text-[58px] leading-tight"
              >
                {title}
              </TextAnimate>
            </RevealItem>

            {/* Description */}
            {description && (
              <RevealItem>
                <TextAnimate
                  by="word"
                  animation="fadeIn"
                  duration={1.1}
                  className="text-lg text-neutral-100 leading-relaxed max-w-lg"
                >
                  {description}
                </TextAnimate>
              </RevealItem>
            )}
          </div>

          {/* Course meta — level / language */}
          <RevealItem>
            <div className="flex flex-wrap gap-3">
              {courseMeta.map((item) => (
                <div
                  key={item.label}
                  className="flex items-center gap-2 rounded-full border border-white/20 bg-white/8 px-4 py-2 text-sm text-white backdrop-blur-sm"
                >
                  <item.icon className="text-primary-400 text-base" />
                  <span className="text-white/60 text-xs">{item.label}:</span>
                  <span className="font-semibold">{item.value}</span>
                </div>
              ))}
            </div>
          </RevealItem>

          {/* CTA */}
          <RevealItem>
            <Link
              to="/subscribe"
              search={course?.slug ? { course: course.slug } : {}}
            >
              <Button size="lg" className="gap-2 px-8 py-6 text-base">
                <LuSend className="text-lg" />
                Join This Course
              </Button>
            </Link>
          </RevealItem>
        </ScrollReveal>

        {/* ── Right column — masked image ── */}
        <ScrollReveal
          className="max-w-120 lg:max-w-140 mx-auto relative"
          stagger={0.1}
        >
          <RevealItem>
            <div className="relative">
              <div className="relative overflow-hidden course-img-mask">
                <img
                  alt={title}
                  className="object-contain bg-white object-center aspect-square w-full"
                  decoding="async"
                  loading="eager"
                  src={image}
                />
                <div className="absolute inset-0 bg-primary-900/50" />
                <img
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 aspect-square w-full"
                  decoding="async"
                  loading="eager"
                  src={courseImgOutline}
                />
              </div>

              {/* Status indicator */}
              <div className="animate-float-y absolute left-2 top-16 flex items-center gap-3 rounded-2xl border border-white/15 bg-white/10 px-4 py-3 shadow-2xl backdrop-blur-md max-lg:hidden">
                <span className="relative flex size-3">
                  <span className="absolute inline-flex size-full animate-ping rounded-full bg-green-400 opacity-75" />
                  <span className="relative inline-flex size-3 rounded-full bg-green-500" />
                </span>
                <div className="flex flex-col leading-tight">
                  <span className="text-xs font-semibold uppercase tracking-widest text-white/60">
                    Status
                  </span>
                  <span className="text-sm font-bold text-white">
                    Available Now
                  </span>
                </div>
              </div>
            </div>
          </RevealItem>
        </ScrollReveal>
      </div>
    </section>
  )
}
